import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { TimeSpan, type ITimeSpan } from '@/objects/TimeSpan';
import { CategoryGroupConnection } from './CategoryGroupConnection';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso, formatDateToTimeJson } from '@/utilities/dateutils';

export class GroupWithCount extends BaseObj {
    public ID: number;
    public __id: number;

    public BibFrom: number | null;
    public __bibfrom: number | null;

    public BibTo: number | null;
    public __bibto: number | null;

    public CategoryGroupConnection: CategoryGroupConnection[];
    public __categorygroupconnection: CategoryGroupConnection[];

    public EventID: number;
    public __eventid: number;

    public GrossTime: boolean;
    public __grosstime: boolean;

    public HideFromLive: boolean;
    public __hidefromlive: boolean;

    public MaxContestants: number | null;
    public __maxcontestants: number | null;

    public MinContestants: number | null;
    public __mincontestants: number | null;

    public Name: string;
    public __name: string;

    public NumberOfLaps: number | null;
    public __numberoflaps: number | null;

    public NumContestants: number;
    public __numcontestants: number;

    public RaceID: number;
    public __raceid: number;

    public ReserveBibs: number;
    public __reservebibs: number;

    public ReserveBibsInFront: boolean;
    public __reservebibsinfront: boolean;

    public SimultaneousStarts: number | null;
    public __simultaneousstarts: number | null;

    public Sort: number;
    public __sort: number;

    public StartDelay: ITimeSpan | null;
    public __startdelay: ITimeSpan | null;

    public StartInterval: ITimeSpan | null;
    public __startinterval: ITimeSpan | null;

    public StartTime: Date | null;
    public __starttime: Date | null;

    public TimeBeforeRoundCount: Date | null;
    public __timebeforeroundcount: Date | null;

    public UpdateRaceContestantStartWhenSaving: boolean;
    public __updateracecontestantstartwhensaving: boolean;

    public WaveStart: Date | null;
    public __wavestart: Date | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        BibFrom?: number | null;
        BibTo?: number | null;
        CategoryGroupConnection?: CategoryGroupConnection[];
        EntityState?: EntityState;
        EventID?: number;
        GrossTime?: boolean;
        HideFromLive?: boolean;
        MaxContestants?: number | null;
        MinContestants?: number | null;
        Name?: string | null;
        NumberOfLaps?: number | null;
        NumContestants?: number;
        RaceID?: number;
        ReserveBibs?: number;
        ReserveBibsInFront?: boolean;
        SimultaneousStarts?: number | null;
        Sort?: number;
        StartDelay?: ITimeSpan | null;
        StartInterval?: ITimeSpan | null;
        StartTime?: Date | null;
        TimeBeforeRoundCount?: Date | null;
        UpdateRaceContestantStartWhenSaving?: boolean;
        WaveStart?: Date | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<GroupWithCount>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('GroupWithCount tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.BibFrom = values.BibFrom ?? null;
        this.__bibfrom = values.BibFrom ?? null;
        Object.defineProperty(this, 'BibFrom', {
            get(): number | null {
                return this.__bibfrom;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('BibFrom', this.__bibfrom, value);
                this.__bibfrom = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.BibTo = values.BibTo ?? null;
        this.__bibto = values.BibTo ?? null;
        Object.defineProperty(this, 'BibTo', {
            get(): number | null {
                return this.__bibto;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('BibTo', this.__bibto, value);
                this.__bibto = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CategoryGroupConnection = values.CategoryGroupConnection ?? [];
        this.__categorygroupconnection = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.CategoryGroupConnection ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('CategoryGroupConnection', newVal),
        });
        this.__categorygroupconnection.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('CategoryGroupConnection', this.CategoryGroupConnection),
            );
        });
        Object.defineProperty(this, 'CategoryGroupConnection', {
            get(): CategoryGroupConnection[] {
                return this.__categorygroupconnection;
            },
            set(value: CategoryGroupConnection[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__categorygroupconnection = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: CategoryGroupConnection[], newVal: CategoryGroupConnection[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('CategoryGroupConnection', newVal),
                });
                this.__categorygroupconnection.forEach((child: CategoryGroupConnection) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('CategoryGroupConnection', this.CategoryGroupConnection),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('CategoryGroupConnection', this.CategoryGroupConnection);
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GrossTime = values.GrossTime ?? false;
        this.__grosstime = values.GrossTime ?? false;
        Object.defineProperty(this, 'GrossTime', {
            get(): boolean {
                return this.__grosstime;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('GrossTime', this.__grosstime, value);
                this.__grosstime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.HideFromLive = values.HideFromLive ?? false;
        this.__hidefromlive = values.HideFromLive ?? false;
        Object.defineProperty(this, 'HideFromLive', {
            get(): boolean {
                return this.__hidefromlive;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('HideFromLive', this.__hidefromlive, value);
                this.__hidefromlive = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.MaxContestants = values.MaxContestants ?? null;
        this.__maxcontestants = values.MaxContestants ?? null;
        Object.defineProperty(this, 'MaxContestants', {
            get(): number | null {
                return this.__maxcontestants;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('MaxContestants', this.__maxcontestants, value);
                this.__maxcontestants = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.MinContestants = values.MinContestants ?? null;
        this.__mincontestants = values.MinContestants ?? null;
        Object.defineProperty(this, 'MinContestants', {
            get(): number | null {
                return this.__mincontestants;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('MinContestants', this.__mincontestants, value);
                this.__mincontestants = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? '';
        this.__name = values.Name ?? '';
        Object.defineProperty(this, 'Name', {
            get(): string {
                return this.__name;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.NumberOfLaps = values.NumberOfLaps ?? null;
        this.__numberoflaps = values.NumberOfLaps ?? null;
        Object.defineProperty(this, 'NumberOfLaps', {
            get(): number | null {
                return this.__numberoflaps;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('NumberOfLaps', this.__numberoflaps, value);
                this.__numberoflaps = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.NumContestants = values.NumContestants ?? 0;
        this.__numcontestants = values.NumContestants ?? 0;
        Object.defineProperty(this, 'NumContestants', {
            get(): number {
                return this.__numcontestants;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('NumContestants', this.__numcontestants, value);
                this.__numcontestants = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceID = values.RaceID ?? 0;
        this.__raceid = values.RaceID ?? 0;
        Object.defineProperty(this, 'RaceID', {
            get(): number {
                return this.__raceid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceID', this.__raceid, value);
                this.__raceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ReserveBibs = values.ReserveBibs ?? 0;
        this.__reservebibs = values.ReserveBibs ?? 0;
        Object.defineProperty(this, 'ReserveBibs', {
            get(): number {
                return this.__reservebibs;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ReserveBibs', this.__reservebibs, value);
                this.__reservebibs = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ReserveBibsInFront = values.ReserveBibsInFront ?? false;
        this.__reservebibsinfront = values.ReserveBibsInFront ?? false;
        Object.defineProperty(this, 'ReserveBibsInFront', {
            get(): boolean {
                return this.__reservebibsinfront;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ReserveBibsInFront', this.__reservebibsinfront, value);
                this.__reservebibsinfront = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SimultaneousStarts = values.SimultaneousStarts ?? null;
        this.__simultaneousstarts = values.SimultaneousStarts ?? null;
        Object.defineProperty(this, 'SimultaneousStarts', {
            get(): number | null {
                return this.__simultaneousstarts;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('SimultaneousStarts', this.__simultaneousstarts, value);
                this.__simultaneousstarts = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Sort = values.Sort ?? 0;
        this.__sort = values.Sort ?? 0;
        Object.defineProperty(this, 'Sort', {
            get(): number {
                return this.__sort;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Sort', this.__sort, value);
                this.__sort = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartDelay = values.StartDelay ?? null;
        this.__startdelay = values.StartDelay ?? null;
        Object.defineProperty(this, 'StartDelay', {
            get(): ITimeSpan | null {
                return this.__startdelay;
            },
            set(value: ITimeSpan | null) {
                this.__entityStateHandler.propertyChanged('StartDelay', this.__startdelay, value);
                this.__startdelay = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartInterval = values.StartInterval ?? null;
        this.__startinterval = values.StartInterval ?? null;
        Object.defineProperty(this, 'StartInterval', {
            get(): ITimeSpan | null {
                return this.__startinterval;
            },
            set(value: ITimeSpan | null) {
                this.__entityStateHandler.propertyChanged('StartInterval', this.__startinterval, value);
                this.__startinterval = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartTime = values.StartTime ?? null;
        this.__starttime = values.StartTime ?? null;
        Object.defineProperty(this, 'StartTime', {
            get(): Date | null {
                return this.__starttime;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('StartTime', this.__starttime, value);
                this.__starttime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TimeBeforeRoundCount = values.TimeBeforeRoundCount ?? null;
        this.__timebeforeroundcount = values.TimeBeforeRoundCount ?? null;
        Object.defineProperty(this, 'TimeBeforeRoundCount', {
            get(): Date | null {
                return this.__timebeforeroundcount;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('TimeBeforeRoundCount', this.__timebeforeroundcount, value);
                this.__timebeforeroundcount = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.UpdateRaceContestantStartWhenSaving = values.UpdateRaceContestantStartWhenSaving ?? false;
        this.__updateracecontestantstartwhensaving = values.UpdateRaceContestantStartWhenSaving ?? false;
        Object.defineProperty(this, 'UpdateRaceContestantStartWhenSaving', {
            get(): boolean {
                return this.__updateracecontestantstartwhensaving;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged(
                    'UpdateRaceContestantStartWhenSaving',
                    this.__updateracecontestantstartwhensaving,
                    value,
                );
                this.__updateracecontestantstartwhensaving = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.WaveStart = values.WaveStart ?? null;
        this.__wavestart = values.WaveStart ?? null;
        Object.defineProperty(this, 'WaveStart', {
            get(): Date | null {
                return this.__wavestart;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('WaveStart', this.__wavestart, value);
                this.__wavestart = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"BibFrom": ${JSON.stringify(this.BibFrom)}` +
            ',' +
            `"BibTo": ${JSON.stringify(this.BibTo)}` +
            ',' +
            `"CategoryGroupConnection": [ ${this.CategoryGroupConnection.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"GrossTime": ${JSON.stringify(this.GrossTime)}` +
            ',' +
            `"HideFromLive": ${JSON.stringify(this.HideFromLive)}` +
            ',' +
            `"MaxContestants": ${JSON.stringify(this.MaxContestants)}` +
            ',' +
            `"MinContestants": ${JSON.stringify(this.MinContestants)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"NumberOfLaps": ${JSON.stringify(this.NumberOfLaps)}` +
            ',' +
            `"NumContestants": ${JSON.stringify(this.NumContestants)}` +
            ',' +
            `"RaceID": ${JSON.stringify(this.RaceID)}` +
            ',' +
            `"ReserveBibs": ${JSON.stringify(this.ReserveBibs)}` +
            ',' +
            `"ReserveBibsInFront": ${JSON.stringify(this.ReserveBibsInFront)}` +
            ',' +
            `"SimultaneousStarts": ${JSON.stringify(this.SimultaneousStarts)}` +
            ',' +
            `"Sort": ${JSON.stringify(this.Sort)}` +
            ',' +
            `"StartDelay": ${this.StartDelay ? '"' + this.StartDelay?.serialize() + '"' : null}` +
            ',' +
            `"StartInterval": ${this.StartInterval ? '"' + this.StartInterval?.serialize() + '"' : null}` +
            ',' +
            `"StartTime": ${this.StartTime ? '"' + formatDateIso(this.StartTime) + '"' : null}` +
            ',' +
            `"TimeBeforeRoundCount": ${this.TimeBeforeRoundCount ? '"' + formatDateToTimeJson(this.TimeBeforeRoundCount) + '"' : null}` +
            ',' +
            `"UpdateRaceContestantStartWhenSaving": ${JSON.stringify(this.UpdateRaceContestantStartWhenSaving)}` +
            ',' +
            `"WaveStart": ${this.WaveStart ? '"' + formatDateIso(this.WaveStart) + '"' : null}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of GroupWithCount from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        BibFrom?: number | null;
        BibTo?: number | null;
        CategoryGroupConnection?: CategoryGroupConnection[];
        EventID?: number;
        GrossTime?: boolean;
        HideFromLive?: boolean;
        MaxContestants?: number | null;
        MinContestants?: number | null;
        Name?: string | null;
        NumberOfLaps?: number | null;
        NumContestants?: number;
        RaceID?: number;
        ReserveBibs?: number;
        ReserveBibsInFront?: boolean;
        SimultaneousStarts?: number | null;
        Sort?: number;
        StartDelay?: ITimeSpan | null;
        StartInterval?: ITimeSpan | null;
        StartTime?: Date | null;
        TimeBeforeRoundCount?: Date | null;
        UpdateRaceContestantStartWhenSaving?: boolean;
        WaveStart?: Date | null;
        EntityState?: 'Added' | 'Unchanged';
    }): GroupWithCount {
        return new GroupWithCount(values);
    }

    public static _FromServerJson(json: any): GroupWithCount {
        return new GroupWithCount({
            ID: json.ID,
            BibFrom: json.BibFrom ?? null,
            BibTo: json.BibTo ?? null,
            CategoryGroupConnection: json.CategoryGroupConnection?.map((x: any) => CategoryGroupConnection._FromServerJson(x)),
            EventID: json.EventID,
            GrossTime: json.GrossTime,
            HideFromLive: json.HideFromLive,
            MaxContestants: json.MaxContestants ?? null,
            MinContestants: json.MinContestants ?? null,
            Name: json.Name ?? '',
            NumberOfLaps: json.NumberOfLaps ?? null,
            NumContestants: json.NumContestants,
            RaceID: json.RaceID,
            ReserveBibs: json.ReserveBibs,
            ReserveBibsInFront: json.ReserveBibsInFront,
            SimultaneousStarts: json.SimultaneousStarts ?? null,
            Sort: json.Sort,
            StartDelay: json.StartDelay ? TimeSpan.FromTimeString(json.StartDelay) : null,
            StartInterval: json.StartInterval ? TimeSpan.FromTimeString(json.StartInterval) : null,
            StartTime: json.StartTime ? (json.StartTime instanceof Date ? json.StartTime : new Date(json.StartTime)) : null,
            TimeBeforeRoundCount: json.TimeBeforeRoundCount ? new Date('1970-01-01 ' + json.TimeBeforeRoundCount) : null,
            UpdateRaceContestantStartWhenSaving: json.UpdateRaceContestantStartWhenSaving,
            WaveStart: json.WaveStart ? (json.WaveStart instanceof Date ? json.WaveStart : new Date(json.WaveStart)) : null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of GroupWithCount from another instance of GroupWithCount.
     * @param other The GroupWithCount you want to clone
     * @returns A cloned GroupWithCount
     */
    public static FromOther(other: GroupWithCount): GroupWithCount {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return GroupWithCount._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return GroupWithCount.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'BibFrom'
            | 'BibTo'
            | 'CategoryGroupConnection'
            | 'EventID'
            | 'GrossTime'
            | 'HideFromLive'
            | 'MaxContestants'
            | 'MinContestants'
            | 'Name'
            | 'NumberOfLaps'
            | 'NumContestants'
            | 'RaceID'
            | 'ReserveBibs'
            | 'ReserveBibsInFront'
            | 'SimultaneousStarts'
            | 'Sort'
            | 'StartDelay'
            | 'StartInterval'
            | 'StartTime'
            | 'TimeBeforeRoundCount'
            | 'UpdateRaceContestantStartWhenSaving'
            | 'WaveStart'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'GroupWithCount';
}
